/**
 * Embedding Provider Interface
 *
 * Specialized interface for embedding operations.
 * Some providers (like Anthropic) don't provide embeddings,
 * so we separate this concern from LLM completions.
 */

export interface EmbeddingProviderRequest {
  input: string | string[];
  model: string;
}

export interface EmbeddingProviderResponse {
  embeddings: number[][] | number[];
  model: string;
  usage: {
    prompt_tokens: number;
    total_tokens: number;
  };
}

/**
 * IEmbeddingProvider Interface
 *
 * Defines contract for embedding provider implementations.
 */
export interface IEmbeddingProvider {
  /**
   * Generate embedding for single text
   *
   * @param request - Embedding request
   * @returns Promise with embedding vector
   */
  generateEmbedding(request: EmbeddingProviderRequest): Promise<EmbeddingProviderResponse>;

  /**
   * Generate embeddings for multiple texts
   *
   * @param request - Embedding request with array of strings
   * @returns Promise with embedding vectors
   */
  generateBatchEmbeddings(request: EmbeddingProviderRequest): Promise<EmbeddingProviderResponse>;

  /**
   * Get provider name
   */
  getName(): string;

  /**
   * Check if provider supports embeddings
   */
  supportsEmbeddings(): boolean;

  /**
   * Get embedding model details
   */
  getModelInfo(model: string): {
    dimensions: number;
    maxInputTokens: number;
    costPer1MTokens: number;
  };
}

/**
 * Embedding dimension constants per provider
 */
export const EMBEDDING_DIMENSIONS = {
  OPENAI_TEXT_EMBEDDING_3_SMALL: 1536,
  OPENAI_TEXT_EMBEDDING_3_LARGE: 3072,
  GEMINI_EMBEDDING: 768,
  COHERE_EMBED_ENGLISH_V3_SMALL: 384,
  COHERE_EMBED_ENGLISH_V3_LARGE: 1024,
} as const;

/**
 * Model compatibility mapping
 * Shows which embedding providers support which models
 */
export const MODEL_COMPATIBILITY = {
  'text-embedding-3-small': {
    provider: 'openai',
    dimensions: EMBEDDING_DIMENSIONS.OPENAI_TEXT_EMBEDDING_3_SMALL,
  },
  'text-embedding-3-large': {
    provider: 'openai',
    dimensions: EMBEDDING_DIMENSIONS.OPENAI_TEXT_EMBEDDING_3_LARGE,
  },
  'embedding-001': {
    provider: 'gemini',
    dimensions: EMBEDDING_DIMENSIONS.GEMINI_EMBEDDING,
  },
} as const;
